import React, { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import * as styles from './EditableCriteria.module.scss';
import UnsavedChangesModal from './UnsavedChangesModal';
import { Button } from 'components/core';
import { Inline, Stack } from 'components/layout';
import { SidebarBody, SidebarFooter } from 'components/shared/Sidebar';
import CriteriaFieldsets from 'components/shared/CriteriaFieldsets';
import { CriteriaFormValues } from 'concepts/Criteria/save/useSaveCriteria';
import { CriteriaFramework } from 'concepts/Criteria/framework/CriteriaFramework';

// Everything here is handed down by useCriteriaDraft, which owns the state
// above the presentation branch, so this panel can remount freely (the
// 799/800px presentation flip) without losing typed input.
export type CriteriaEditPanelProps = {
  form: UseFormReturn<CriteriaFormValues>;
  framework?: CriteriaFramework;
  mergeGeneration: number;
  onSave: () => void;
  onCancel: () => void;
  unsavedChanges: {
    isOpen: boolean;
    onSave: () => void;
    onDiscard: () => void;
    onKeepEditing: () => void;
  };
};

// Presentational only: every piece of draft state lives in
// useCriteriaDraft, above the presentation branch, so this panel can
// remount on the 799/800px flip without losing typed input.
const CriteriaEditPanel = ({
  form,
  framework,
  mergeGeneration,
  onSave,
  onCancel,
  unsavedChanges,
}: CriteriaEditPanelProps) => {
  const { isSubmitting } = form.formState;

  // Saving disables (and so blurs) Save; a failed or conflicted save
  // leaves the panel open, so hand focus back. A successful save unmounts
  // the panel before isSubmitting clears, so this never fires on that
  // path.
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const wasSubmittingRef = useRef(false);
  useEffect(() => {
    const wasSubmitting = wasSubmittingRef.current;
    if (wasSubmitting && !isSubmitting) {
      saveButtonRef.current?.focus();
    }
    wasSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  return (
    <>
      <SidebarBody className={styles.HeaderDivider}>
        <Stack spacing={4}>
          <CriteriaFieldsets
            form={form}
            framework={framework}
            layout="sidebar"
            mergeGeneration={mergeGeneration}
            disabled={isSubmitting}
          />
        </Stack>
      </SidebarBody>
      <SidebarFooter>
        <Inline alignX="left" spacing={3}>
          {/* Save sits outside the scrollable fields, so it submits via
              onClick rather than form association. */}
          <Button
            ref={saveButtonRef}
            size="sm"
            type="brand"
            onClick={onSave}
            isLoading={isSubmitting}
            className={styles.SidebarFooterButton}
          >
            Save
          </Button>
          {/* Cancel can't stop the in-flight POST, and the pop-out host's
              cancel-refetch would race the save response. */}
          <Button
            size="sm"
            type="neutral"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
            className={styles.SidebarFooterButton}
          >
            Cancel
          </Button>
        </Inline>
      </SidebarFooter>
      {/* Mounted only whilst open: answering must tear the dialog down in
          the same commit, or its focus trap outlives the answer and steals
          the focus the next panel has just placed. */}
      {unsavedChanges.isOpen && (
        <UnsavedChangesModal
          isOpen
          onSave={unsavedChanges.onSave}
          onDiscard={unsavedChanges.onDiscard}
          onKeepEditing={unsavedChanges.onKeepEditing}
        />
      )}
    </>
  );
};

export default CriteriaEditPanel;
